import { useQuery } from "convex/react";
import {
  Building2,
  CheckCircle,
  FileText,
  Inbox,
  Loader2,
  User,
} from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Navbar,
  PageContainer,
  Progress,
  StatutNotationBadge,
} from "@repo/ui";
import { useEvaluatorAuthStore, useEvaluatorLogout } from "@repo/auth";
import { getEvaluationTitle } from "@repo/utils";
import { StatutNotationEnum, TypeEvalEnum } from "@convex/schema";

type EvaluationANoter = {
  notationId: Id<"notation">;
  evaluationId: Id<"evaluations">;
  typeEval: string;
  template?: string;
  statutNotation: StatutNotationEnum;
  statutEvaluation: string;
};

type CandidatANoter = {
  parcoursCandidatId?: Id<"parcoursEvaluations">;
  nom?: string;
  prenom?: string;
  titre: string;
  // Contribution au référentiel d'une campagne : le référent décrit son
  // organisation, il ne note aucun candidat (§8.3).
  estReferentiel?: boolean;
  cibleReferentiel?: "pc-fit" | "pami";
  evaluations: EvaluationANoter[];
  progression: { total: number; terminees: number; pourcentage: number };
};

export function EvaluateurView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = useEvaluatorAuthStore((s) => s.token);
  const firstName = useEvaluatorAuthStore((s) => s.firstName);
  const email = useEvaluatorAuthStore((s) => s.email);
  const { logout } = useEvaluatorLogout();

  const result = useQuery(
    api.notation.portail.obtenirParcoursEvaluateurComplet,
    id && token
      ? { parcoursEvaluateurId: id as Id<"parcoursEvaluateur">, identifiant: token }
      : "skip",
  );

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if ("erreur" in result) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Erreur</h1>
          <p className="mt-2 text-muted-foreground">{result.erreur}</p>
        </div>
      </div>
    );
  }

  const { evaluateur, parcours, campagne } = result;

  // Le référentiel se remplit DANS ce portail ; les notations de candidats
  // s'ouvrent dans l'app de notation externe.
  const handleEvaluationClick = (
    notationId: Id<"notation">,
    estReferentiel?: boolean,
  ) => {
    if (estReferentiel) {
      void navigate(`/pe/${id}/referentiel/${notationId}`);
      return;
    }
    const base = process.env.BUN_PUBLIC_APP_NOTATION_URL;
    if (!base) return;
    const tokenParam =
      process.env.NODE_ENV === "development" && token
        ? `?token=${encodeURIComponent(token)}`
        : "";
    window.open(`${base}/e/${notationId}${tokenParam}`, "_blank");
  };

  const getEvaluationIcon = (typeEval: string) =>
    typeEval === TypeEvalEnum.INBASKET ? (
      <Inbox className="h-5 w-5 text-muted-foreground" />
    ) : (
      <FileText className="h-5 w-5 text-muted-foreground" />
    );

  const getTitleForEvaluation = (evaluation: EvaluationANoter) =>
    getEvaluationTitle(evaluation, "candidat");

  const nomCandidat = (candidat: CandidatANoter) =>
    [candidat.prenom, candidat.nom].filter(Boolean).join(" ") || candidat.titre;

  // Un référentiel n'est pas une notation de candidat : le référent décrit son
  // organisation. Les libellés diffèrent selon la cible (§8.3).
  const titreReferentiel = (cible?: "pc-fit" | "pami") =>
    cible === "pami"
      ? "Attentes du poste (motivations)"
      : cible === "pc-fit"
        ? "Priorités culturelles de l'organisation"
        : "Référentiel de la campagne";

  const sousTitreReferentiel = (cible?: "pc-fit" | "pami") =>
    cible === "pami"
      ? "Plages de score attendues pour réussir dans ce poste"
      : cible === "pc-fit"
        ? "Ce qui compte le plus pour réussir dans votre organisation"
        : "Attentes de votre organisation pour ce poste";

  const libelleAction = (
    statut: StatutNotationEnum,
    estReferentiel?: boolean,
  ) => {
    if (statut === StatutNotationEnum.TERMINEE)
      return estReferentiel ? "Consulter" : "Voir";
    if (statut === StatutNotationEnum.EN_COURS) return "Continuer";
    return estReferentiel ? "Renseigner" : "Noter";
  };

  const displayName =
    evaluateur.anonyme === true
      ? token || ""
      : `${firstName} ${evaluateur.nomEvaluateur ?? ""}`.trim() || token || "";

  const initials = displayName.charAt(0).toUpperCase() || "?";

  const tousLesGroupes = parcours.flatMap((p) => p.candidats);
  const nbCandidatsTotal = tousLesGroupes.filter((c) => !c.estReferentiel).length;
  const nbReferentiels = tousLesGroupes.filter((c) => c.estReferentiel).length;
  // Trois cas : notation seule, contribution seule, mixte. L'en-tête ne doit
  // pas annoncer des « candidats à évaluer » à un référent qui n'en a aucun.
  const seulementReferentiel = nbReferentiels > 0 && nbCandidatsTotal === 0;

  const titrePage = seulementReferentiel
    ? "Mes contributions"
    : nbReferentiels > 0
      ? "Mes parcours"
      : "Mes parcours d'évaluation";

  const resumeParcours = () => {
    if (parcours.length === 0)
      return "Aucun parcours ne vous est actuellement assigné.";
    const parts: string[] = [`${parcours.length} parcours`];
    if (nbCandidatsTotal > 0)
      parts.push(
        `${nbCandidatsTotal} candidat${nbCandidatsTotal > 1 ? "s" : ""} à évaluer`,
      );
    if (nbReferentiels > 0)
      parts.push(
        `${nbReferentiels} référentiel${nbReferentiels > 1 ? "s" : ""} à renseigner`,
      );
    return `${parts.join(" · ")}.`;
  };

  const toutTermine =
    parcours.length > 0 &&
    parcours.every((p) => p.progression.pourcentage === 100);

  return (
    <div className="min-h-screen bg-background">
      <Navbar
        title={seulementReferentiel ? "Mon espace" : "Mon espace d'évaluation"}
        userInfo={{
          displayName,
          subtitle: evaluateur.anonyme === true ? "" : email || "",
          initials,
        }}
        onLogout={logout}
      />

      <main>
        <PageContainer>
          <div className="mb-8">
            {/* Un lien = une campagne : on la nomme pour lever toute ambiguïté
                quand l'évaluateur est sollicité sur plusieurs campagnes. */}
            {campagne && (
              <p className="text-sm font-medium text-primary">{campagne.nom}</p>
            )}
            <h2 className="text-3xl font-bold text-gray-900">{titrePage}</h2>
            <p className="mt-2 text-gray-600">{resumeParcours()}</p>
          </div>

          <div className="space-y-10">
            {parcours.map((p) => (
              <section key={p.parcoursEvaluateurId}>
                <div className="mb-4 flex items-center justify-between gap-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {p.titre}
                  </h3>
                  <div className="flex items-center gap-2 min-w-35">
                    <Progress
                      value={p.progression.pourcentage}
                      className={`h-2 flex-1 ${p.progression.pourcentage === 100 ? "[&>div]:bg-green-500" : ""}`}
                    />
                    <span
                      className={`text-sm font-medium whitespace-nowrap ${p.progression.pourcentage === 100 ? "text-green-600" : ""}`}
                    >
                      {p.progression.pourcentage}%
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  {p.candidats.map((candidat, idx) => (
                    <Card
                      key={candidat.parcoursCandidatId ?? `sans-${idx}`}
                    >
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-surface-3 rounded-controle shrink-0">
                            {candidat.estReferentiel ? (
                              <Building2 className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <User className="h-5 w-5 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <CardTitle>
                              {candidat.estReferentiel
                                ? candidat.titre
                                : nomCandidat(candidat)}
                            </CardTitle>
                            <CardDescription>
                              {candidat.estReferentiel
                                ? sousTitreReferentiel(candidat.cibleReferentiel)
                                : candidat.titre}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {candidat.evaluations.map((evaluation) => (
                            <div
                              key={evaluation.notationId}
                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-charte-line rounded-surface transition-colors duration-150 hover:border-charte-line-accent"
                            >
                              <div className="flex items-center gap-4">
                                <div className="p-2 bg-surface-3 rounded-controle shrink-0">
                                  {getEvaluationIcon(evaluation.typeEval)}
                                </div>
                                <div>
                                  <h4 className="font-medium text-gray-900">
                                    {candidat.estReferentiel
                                      ? titreReferentiel(candidat.cibleReferentiel)
                                      : getTitleForEvaluation(evaluation)}
                                  </h4>
                                </div>
                              </div>

                              <div className="flex items-center gap-4 sm:shrink-0">
                                <StatutNotationBadge
                                  statut={evaluation.statutNotation}
                                  aFaire
                                />
                                <Button
                                  className="flex-1 sm:flex-none"
                                  onClick={() =>
                                    handleEvaluationClick(
                                      evaluation.notationId,
                                      candidat.estReferentiel,
                                    )
                                  }
                                  variant={
                                    evaluation.statutNotation ===
                                    StatutNotationEnum.TERMINEE
                                      ? "outline"
                                      : "default"
                                  }
                                >
                                  {libelleAction(
                                    evaluation.statutNotation,
                                    candidat.estReferentiel,
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {toutTermine && (
            <Card className="mt-8 border-green-200 bg-linear-to-r from-green-50 to-emerald-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full shrink-0">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="text-green-800 font-medium">
                    {seulementReferentiel
                      ? "Votre contribution est complète. Merci !"
                      : nbReferentiels > 0
                        ? "Vous avez terminé vos évaluations et vos contributions. Merci !"
                        : "Vous avez terminé toutes vos évaluations. Merci !"}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </PageContainer>
      </main>
    </div>
  );
}
